<template>
  <DrawerRoot v-model:open="isOpen">
    <DrawerTrigger v-if="$slots.trigger" asChild>
      <slot name="trigger" />
    </DrawerTrigger>

    <DrawerPortal>
      <Transition name="overlay">
        <DrawerOverlay v-if="isOpen" class="PanelDrawer-overlay" />
      </Transition>

      <DrawerContent class="PanelDrawer">
        <DrawerHandle class="PanelDrawer-handle" />

        <div class="PanelDrawer-inner">
          <div v-if="title" class="PanelDrawer-header">
            <DrawerTitle class="PanelDrawer-title">
              <slot name="title">
                {{ title }}
              </slot>
            </DrawerTitle>

            <DrawerClose class="PanelDrawer-closeButton" :aria-label="t('ui.a11y.close')">
              <Icon icon="window-close" :size="16" />
            </DrawerClose>
          </div>

          <div class="PanelDrawer-body">
            <slot />
          </div>

          <div class="PanelDrawer-footer">
            <slot name="footer" />
          </div>
        </div>
      </DrawerContent>
    </DrawerPortal>
  </DrawerRoot>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import {
  DrawerRoot,
  DrawerPortal,
  DrawerOverlay,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
  DrawerHandle,
  DrawerClose,
} from 'reka-ui';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import Icon from '../../atoms/Icon/Icon.vue';
import type { PanelDrawerProps } from './PanelDrawer.model';

defineProps<PanelDrawerProps>();

const isOpen = defineModel<boolean>();

const { t } = useI18n();

watch(isOpen, (newVal) => {
  if (newVal) {
    // Fixes pointer-events being disabled on body which affects 3rd party modals
    // https://github.com/unovue/reka-ui/issues/1540
    setTimeout(() => {
      document.body.style.pointerEvents = '';
    });
  }
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/mixins';
@use '@cvo/ui/styles/utils/animations';

@layer cvo-ui {
  .PanelDrawer {
    position: fixed;
    inset-block-end: 0;
    inset-inline: 0;
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-6;
    padding-block-start: vars.$dimensions-d-3;
    padding-block-end: vars.$dimensions-d-10;
    padding-inline: vars.$dimensions-d-6;
    background-color: vars.$organisms-drawer-bg-color;
    border: vars.$dimensions-d-0-25 solid vars.$organisms-drawer-border-color;
    border-radius: vars.$dimensions-d-6 vars.$dimensions-d-6 0 0;
    box-shadow: vars.$shadow-drawer;
    align-items: flex-start;
    max-height: 90dvh;
    overflow: hidden;
    // Follows the pointer while dragging; reka-ui sets this custom property during swipe
    transform: translateY(var(--drawer-swipe-movement-y, 0px));
    transition: transform vars.$transition-base cubic-bezier(0.32, 0.72, 0, 1);

    &[data-swiping] {
      transition-duration: 0ms;
    }

    &[data-state='open'] {
      animation: PanelDrawer-slideIn vars.$transition-base cubic-bezier(0.32, 0.72, 0, 1);
    }

    &[data-state='closed'] {
      animation: PanelDrawer-slideOut vars.$transition-base cubic-bezier(0.7, 0, 0.84, 0);
    }

    &-overlay {
      @include mixins.set-overlay;
    }

    &-handle {
      width: 40px;
      height: 4px;
      border-radius: 2px;
      background-color: vars.$organisms-drawer-handle-color;
      align-self: center;
      flex-shrink: 0;
    }

    &-inner {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-6;
      width: 100%;
      min-height: 0;
      flex: 1;
    }

    &-body {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-6;
      overflow-y: auto;
      min-height: 0;
      flex: 1;
    }

    &-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      padding-block-end: vars.$dimensions-d-2;
      border-block-end: vars.$dimensions-d-0-25 solid vars.$organisms-drawer-divider-border-color;
    }

    &-title {
      @include text.text('sm', vars.$typography-weight-strong);
      letter-spacing: -0.03em;
      line-height: 100%;
      color: vars.$organisms-drawer-title-color;
      margin: 0;
      flex: 1 0 0;
      min-width: 0;
    }

    &-closeButton {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: none;
      background: none;
      appearance: none;
      cursor: pointer;
      color: vars.$organisms-drawer-close-icon-color;
      flex-shrink: 0;

      &:focus-visible {
        outline: 2px solid vars.$color-brand-secondary-900;
        outline-offset: 2px;
      }
    }

    &-footer {
      display: flex;
      width: 100%;
    }
  }

  @keyframes PanelDrawer-slideIn {
    from {
      translate: 0 100%;
    }
  }

  @keyframes PanelDrawer-slideOut {
    to {
      translate: 0 100%;
    }
  }
}
</style>
